import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatMiniFabButton } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { escape } from 'lodash';
import { distinctUntilChanged, EMPTY, filter, Subscription, switchMap } from 'rxjs';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { StatusCategoryBadgeComponent } from '../../../../shared/components/status-category-badge/status-category-badge.component';
import { ELoadStatus } from '../../../../state/state.interface';
import { WorkInstructionEditorComponent } from '../../../settings/work-instructions/work-instruction-editor/work-instruction-editor.component';
import { EStatusBadgeExtraCategory } from '../../items.interface';
import { WorkInstructionStepperComponent } from '../../work-instruction-stepper/work-instruction-stepper.component';
import { IWorkInstructionStepperParams } from '../../work-instruction-stepper/work-instruction-stepper.interface';
import { ApplyChangesRecursivelyQuestionModalComponent } from '../apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.component';
import {
  EItemRecurrenceEditType,
  ERecursiveAction,
} from '../apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.interface';
import { RecurringItemService } from '../apply-changes-recursively-question-modal/recurring-item.service';
import { WorkInstructionCheckInComponent } from '../work-instruction-check-in/work-instruction-check-in.component';
import { IWorkInstructionCheckInDialogData } from '../work-instruction-check-in/work-instruction-check-in.interface';
import { WorkInstructionHistoryComponent } from '../work-instruction-history/work-instruction-history.component';
import {
  IWorkInstructionCheckInRow,
  IWorkInstructionItemCard,
  IWorkInstructions,
} from './link-work-instruction.interface';
import { LinkWorkInstructionStore } from './link-work-instruction.store';

@Component({
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatMiniFabButton,
    MatIcon,
    MatTooltip,
    MatProgressBar,
    StatusCategoryBadgeComponent,
  ],
  providers: [LinkWorkInstructionStore, RecurringItemService],
  selector: 'app-link-work-instruction',
  styleUrl: './link-work-instruction.component.scss',
  templateUrl: './link-work-instruction.component.html',
})
export class LinkWorkInstructionComponent implements OnInit, OnDestroy, OnChanges {
  @Input() boardId?: number;
  @Input() itemId?: number;
  @Input() itemKey?: string;
  @Input() dueDateForRecurrence?: string | null;
  @Input() loadTrigger!: EventEmitter<void>;
  @Output() hasWorkInstructionsChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private isEditModal = false;
  public readonly EStatusBadgeExtraCategory = EStatusBadgeExtraCategory;
  public linkedWorkInstructions: IWorkInstructions[] = [];
  protected readonly subscriptions: Subscription[] = [];

  constructor(
    public readonly store: LinkWorkInstructionStore,
    public readonly dialogService: MatDialog,
    private readonly translate: TranslateService,
    private readonly recurringItemService: RecurringItemService,
  ) {}

  public ngOnInit(): void {
    this.subscriptions.push(
      this.loadTrigger.subscribe(() => this.loadWorkInstructions()),
      this.store
        .select((state) => state.assignmentLoading)
        .pipe(
          distinctUntilChanged(),
          filter((status) => status === ELoadStatus.success),
        )
        .subscribe(() => this.loadWorkInstructions()),
      this.store
        .select((state) => (state.workInstructions?.length ?? 0) > 0)
        .pipe(distinctUntilChanged())
        .subscribe((hasWorkInstructions: boolean) => this.hasWorkInstructionsChange.emit(hasWorkInstructions)),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.isEditModal = Boolean(this.itemId);

    if (changes['itemKey']?.currentValue !== changes['itemKey']?.previousValue) {
      this.loadWorkInstructions();
    }
  }

  private loadWorkInstructions(): void {
    if (this.itemKey) {
      this.store.loadWorkInstructions(this.itemKey);
    }
  }

  public onRemoveLinkClick(workInstruction: IWorkInstructionItemCard): void {
    if (Boolean(this.dueDateForRecurrence) && workInstruction.assignmentId) {
      this.askApplyChangesRecursively(workInstruction.assignmentId);

      return;
    }

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialogService.open(ConfirmationDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        content: this.translate.instant('page.items.delete.unassignWorkInstruction', {
          context: escape(workInstruction.name),
        }),
        submitButtonText: this.translate.instant('button.unassign'),
        title: this.translate.instant('system.unassign.confirmationTitle', {
          context: this.translate.instant('field.workInstruction'),
        }),
      },
    });

    this.subscriptions.push(
      dialogRef
        .afterClosed()
        .pipe(
          switchMap((result) => {
            if (!result) {
              return EMPTY;
            }

            if (!this.isEditModal) {
              const index: number = this.linkedWorkInstructions.findIndex((link) => {
                return link.id === workInstruction.id;
              });

              if (index !== -1) {
                this.linkedWorkInstructions.splice(index, 1);
              }

              return EMPTY;
            }

            if (workInstruction.assignmentId) {
              this.unassignItemWorkInstruction(workInstruction.assignmentId);
            }

            return EMPTY;
          }),
        )
        .subscribe(),
    );
  }

  public onClickedOpenStepper(workInstructionId: number): void {
    const workInstruction: IWorkInstructionItemCard | undefined = this.store
      .data$()
      .find((item: IWorkInstructionItemCard) => item.id === workInstructionId);

    if (!workInstruction) {
      return;
    }

    const dialogRef: MatDialogRef<unknown> = this.dialogService.open(WorkInstructionStepperComponent, {
      ...WorkInstructionEditorComponent.dialogConfig,
      data: {
        boardId: this.boardId,
        dueDateForRecurrence: this.dueDateForRecurrence,
        itemGuideAssignmentId: workInstruction.assignmentId,
        itemId: this.itemId,
        name: workInstruction.name,
        workInstructionId,
      } as IWorkInstructionStepperParams,
    });

    this.subscriptions.push(dialogRef.afterClosed().subscribe((): void => this.loadWorkInstructions()));
  }

  public openCheckInDialog(row: IWorkInstructionCheckInRow): void {
    if (!this.boardId || !row.assignmentId) {
      return;
    }

    this.subscriptions.push(
      this.dialogService
        .open(WorkInstructionCheckInComponent, {
          autoFocus: false,
          data: {
            boardId: this.boardId,
            itemId: this.itemId,
            workInstructions: [
              { itemGuideAssignmentId: row.assignmentId, name: row.name, steps: row.checkInSteps ?? [] },
            ],
          } satisfies IWorkInstructionCheckInDialogData,
          maxWidth: '100%',
          restoreFocus: false,
          width: EDialogWidth.xSmall,
        })
        .afterClosed()
        .subscribe(() => this.store.loadCheckIns()),
    );
  }

  public openWorkInstructionHistory(workInstructionId: number): void {
    this.dialogService.open(WorkInstructionHistoryComponent, {
      autoFocus: false,
      data: {
        itemId: this.itemId,
        workInstructionId,
      },
      disableClose: true,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.large,
    });
  }

  public ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  private unassignItemWorkInstruction(
    id: number,
    recurrenceType: EItemRecurrenceEditType = EItemRecurrenceEditType.onlyThis,
  ): void {
    this.store.unassignWorkInstructions({
      id,
      ...this.recurringItemService.getStartDateForAttachmentRecurringUpdate(
        recurrenceType,
        this.dueDateForRecurrence ?? null,
      ),
    });
  }

  private askApplyChangesRecursively(assignmentId: number): void {
    this.subscriptions.push(
      this.dialogService
        .open(ApplyChangesRecursivelyQuestionModalComponent, {
          data: { mode: ERecursiveAction.edit },
        })
        .afterClosed()
        .subscribe((selectedRecurrenceEditType: EItemRecurrenceEditType | '' | undefined): void => {
          if (!selectedRecurrenceEditType) {
            return;
          }

          this.unassignItemWorkInstruction(assignmentId, selectedRecurrenceEditType);

          return;
        }),
    );
  }
}
